import {Injectable, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs";
import {SessionModel} from "../orchestration/session.model";
import {AnalyticsService} from "../orchestration/analytics.service";
import {GeolocateService} from "../store/geolocate";
import {AuthService, EntityData, FedeltaImageData, SearchResult} from "@fedelta-pos/data-client";
import {ParamConfig, ParamConfigService} from "../config-support/param-config.service";
import {DeliveryDetails, OrderingContext, TableSelection} from "./ordering-context";
import {DateTime} from "../string-utils/date-time";
import {NotificationService} from "../notification/notification.service";
import {TranslateService} from "ng2-translate";

// @ts-ignore
import {} from "@types/googlemaps";
import {ResponsiveService} from "../orchestration/responsive.service";
import {NavigationService} from "../orchestration/navigation.service";
import {OrchestrationEvents, SessionReset} from "../orchestration/orchestration-events";
import {OrderingContextEvents} from "./ordering-context-events";
import {DealEvents} from "../deal/deal-events";
import {OrderEvents} from "../order/order-events";

import {IdentityData} from "../identity/identity-entity.model";
import {AppData, AppSettingData} from "../orchestration/app-entity.model";
import {CustomerDetails, CustomerDetailsService} from "../customer-info/customer-details.service";
import {ActiveSchedulesSearchModel} from "../schedule/active-schedules-search.model";
import {StoreData, StoreSearchModel} from "../store/store-search.model";
import {FilterProvider} from "../config-support/filter.provider";
import {EntitlementStatus} from "../deal/entitlement-entity.model";
import {AddressData} from "../customer-info/address.data";
import {MenuModel} from "../menu/menu.model";
import {DealModel} from "../deal/deal.model";
import {OrderModel} from "../order/order.model";
import {IdentityModel} from "../identity/identity.model";
import {OrderType} from "../order/order";
import {LoadingService} from "../ui-elements/loading/loading.service";
import TravelMode = google.maps.TravelMode;
import LatLng = google.maps.LatLng;
import DistanceMatrixStatus = google.maps.DistanceMatrixStatus;
import DistanceMatrixResponseElement = google.maps.DistanceMatrixResponseElement;
import DistanceMatrixElementStatus = google.maps.DistanceMatrixElementStatus;
import DistanceMatrixService = google.maps.DistanceMatrixService;
import DistanceMatrixRequest = google.maps.DistanceMatrixRequest;

export class StoreLocation {
    // noinspection JSUnusedGlobalSymbols
    constructor(public storeName: string, public locationName: string, public stores: StoreData[]) {
    }

    /**
     * Returns the store at this store-location which supports the specified order type.
     * Duplicate stores for an order type are not supported - only one will be returned.
     *
     * @param orderType
     */
    getStoreForOrderType(orderType: OrderType): StoreData {
        return orderType ? this.stores.find((store: StoreData) => store.canOrder(orderType)) : undefined;
    }
}

export class OrderingContextStoreSearchFilterProvider implements FilterProvider {
    public static readonly FILTER_IGNORE_EXCLUSIVE: string = "ignoreExclusive";

    private _orderingContextModel: OrderingContextModel;

    public set orderingContextModel(orderingContextModel: OrderingContextModel) {
        this._orderingContextModel = orderingContextModel;
    }

    getFilters(filters: any): any {
        const newFilters: any = {};
        if (this._orderingContextModel) {
            if (!filters[OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE]) {
                if (this._orderingContextModel.exclusiveWebTerminalId) {
                    newFilters[StoreSearchModel.FILTER_SINGLE_STORE_ID] = this._orderingContextModel.exclusiveWebTerminalId;
                } else if (this._orderingContextModel.exclusiveStoreGroup) {
                    newFilters[StoreSearchModel.FILTER_STORE_GROUP] = this._orderingContextModel.exclusiveStoreGroup;
                } else if (this._orderingContextModel.exclusiveWebTerminalIdList) {
                    newFilters[StoreSearchModel.FILTER_STORES] = this._orderingContextModel.exclusiveWebTerminalIdList;
                }
            }

            if (this._orderingContextModel.tableOnlyMode) {
                newFilters[StoreSearchModel.FILTER_ORDER_AT_TABLE_ONLY] = 1;
                newFilters[StoreSearchModel.FILTER_DISABLE_OTHER_ORDER_TYPES] = 1;
            }

            if (this._orderingContextModel.pickupOnlyMode) {
                newFilters[StoreSearchModel.FILTER_ORDER_AHEAD_ONLY] = 1;
                newFilters[StoreSearchModel.FILTER_DISABLE_OTHER_ORDER_TYPES] = 1;
            }

            if (this._orderingContextModel.deliveryOnlyMode) {
                newFilters[StoreSearchModel.FILTER_ORDER_DELIVERY_ONLY] = 1;
                newFilters[StoreSearchModel.FILTER_DISABLE_OTHER_ORDER_TYPES] = 1;
            }

            if (this._orderingContextModel.parentAppURL && !filters[StoreSearchModel.FILTER_LOOKUP_CODE]) {
                newFilters[StoreSearchModel.FILTER_PARENT_APP_URL] = this._orderingContextModel.parentAppURL;
                newFilters[StoreSearchModel.FILTER_LOCAL_APP_URL] = window.location.href.split("?")[0];
            }
        }
        return newFilters;
    }
}

@Injectable()
export class OrderingContextModel implements OnDestroy {

    /**
     * Preselected ordering context derived from url params
     */
    private _preselectedOrderType: OrderType;
    private _tableOnlyMode: boolean;
    private _pickupOnlyMode: boolean;
    private _deliveryOnlyMode: boolean;
    private _preselectedDealCode: string;

    private _preselectedWebTerminalId: number;
    private _preselectedWebTerminalIdList: number[];
    private _exclusiveStoreGroup: string;
    private _exclusiveWebTerminalId: number;
    private _exclusiveWebTerminalIdList: number[];
    private _preselectedStoreGroup: string;

    private _preselectedTableNumber: string;
    private _preselectedTableNumberLabel: string;

    private _disableLocationServices: boolean;

    /**
     * Selected ordering context
     */
    private _parentAppURL: string;
    private _orderType: OrderType;
    private _stores: StoreData[];
    private _deliveryStores: StoreData[];
    private _currentStore: StoreData;
    private _currentStoreLocation: StoreLocation;
    private _singleStoreLocation: StoreLocation;
    private _singleOrderType: OrderType;

    // This is stored as a date object for convenience and formatting flexibility, however it DOES NOT properly represent an instant in time
    // as in seconds since epoch.
    // Rather, it represents a local-date/time in the timezone of the relevant store, which has been parsed AS IF it were in the local timezone.
    // This means that when this date is converted back to a string, whether for display to the user, or for sending to the server,
    // it should always be in a timezone-independent format, which will implicitly be in the timezone of the relevant store.
    // It also means that any modification to this date (e.g. setting the selected time) should be done using values which themselves are
    // also in the same implicit timezone.
    // Furthermore, this date should never be used to compare against a local date.
    public expectedCompletion: Date;

    // Customer details and other order meta-data are defaulted from the logged-in member, but can be modified for the specific order.
    // Theoretically, customer notes can be stored on a member, but the app does not use them in this way at present. They are always set only for a single order.
    private _firstName: string;
    private _lastName: string;
    private _email: string;
    private _mobilePhone: string;
    private _deliveryInstructions: string;
    private _address: AddressData;

    private _firstNameTrusted: boolean = false;
    private _lastNameTrusted: boolean = false;
    private _emailTrusted: boolean = false;
    private _mobilePhoneTrusted: boolean = false;
    private _deliveryInstructionsTrusted: boolean = false;
    private _addressTrusted: boolean = false;

    private _saveAddressForMember: boolean;
    private _customerNotes: string;
    private _showMarketingOptIn: boolean = false;
    private _marketingOptIn: boolean;
    private _tipSelected: boolean;

    private distanceMatrixService: DistanceMatrixService = new DistanceMatrixService()
    ;
    // Avoid injection dependencies on OrderModel, MenuModel, DealModel and IdentityModel
    private _orderModel: OrderModel;
    private _menuModel: MenuModel;
    private _dealModel: DealModel;
    private _identityModel: IdentityModel;

    private subscriptions: Subscription = new Subscription();

    constructor(private sessionModel: SessionModel,
                private storeSearchModel: StoreSearchModel,
                private activeSchedulesSearchModel: ActiveSchedulesSearchModel,
                private navigationService: NavigationService,
                private paramConfigService: ParamConfigService,
                private notificationService: NotificationService,
                private responsiveService: ResponsiveService,
                private loadingService: LoadingService,
                private translate: TranslateService,
                private authService: AuthService,
                private customerDetailsService: CustomerDetailsService,
                private geolocateService: GeolocateService,
                private analyticsService: AnalyticsService) {
        console.log("--- OrderingContextModel");

        // noinspection JSIgnoredPromiseFromCall
        this.initConfig();

        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe((sessionReset: SessionReset) => this.reset(sessionReset)));
        this.subscriptions.add(OrchestrationEvents.firstAppLoad$.subscribe(async (app: AppData) => {
            const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
            if (paramConfig.pickupOnlyMode && app.appSettings.find((setting: AppSettingData) => setting.name === "pickupOnlyImpliesDeliveryOnly" && setting.value === "true")) {
                paramConfig.deliveryOnlyMode = true;
                this._deliveryOnlyMode = true;
            }
        }));
    }

    set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    set menuModel(menuModel: MenuModel) {
        this._menuModel = menuModel;
    }

    set dealModel(dealModel: DealModel) {
        this._dealModel = dealModel;
    }

    set identityModel(identityModel: IdentityModel) {
        this._identityModel = identityModel;
    }

    private async initConfig(): Promise<void> {
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        if (!paramConfig.unknownBeacon) {
            if (paramConfig.exclusiveStore) {
                this._exclusiveWebTerminalId = paramConfig.exclusiveStore;
                this._preselectedWebTerminalId = paramConfig.exclusiveStore;
            } else {
                this._parentAppURL = paramConfig.parentAppURL;
                if (paramConfig.store) {
                    this._preselectedWebTerminalId = paramConfig.store;
                }
                if (paramConfig.exclusiveStoreGroup) {
                    this._exclusiveStoreGroup = paramConfig.exclusiveStoreGroup;
                    this._preselectedStoreGroup = paramConfig.exclusiveStoreGroup;
                } else if (paramConfig.exclusiveStores) {
                    this._exclusiveWebTerminalIdList = paramConfig.exclusiveStores;
                    this._preselectedWebTerminalIdList = paramConfig.exclusiveStores;
                } else if (paramConfig.storeGroup) {
                    this._preselectedStoreGroup = paramConfig.storeGroup;
                } else if (paramConfig.stores) {
                    this._preselectedWebTerminalIdList = paramConfig.stores;
                }
            }

            this._tableOnlyMode = paramConfig.tableOnlyMode;
            this._pickupOnlyMode = paramConfig.pickupOnlyMode;
            this._deliveryOnlyMode = paramConfig.deliveryOnlyMode;
            this._disableLocationServices = paramConfig.disableLocationServices;

            // Order type parameter is only valid when a specific store is preselected
            const orderTypeSpecified: boolean = !!this._preselectedWebTerminalId && !!paramConfig.orderType;

            // Table number parameter is only valid when one or more stores are preselected or table-only-mode is specified
            if ((this._preselectedWebTerminalId || this._preselectedWebTerminalIdList || this._preselectedStoreGroup || this._tableOnlyMode) && paramConfig.tableNumber) {
                this._preselectedTableNumber = paramConfig.tableNumber;
                if (!orderTypeSpecified && !this._pickupOnlyMode && !this._deliveryOnlyMode) {
                    this._preselectedOrderType = OrderType.ORDER_AT_TABLE;
                }
            }
            if (orderTypeSpecified) {
                this._preselectedOrderType = paramConfig.orderType;
            }
            this._preselectedDealCode = paramConfig.dealCode;
        }
    }

    private reset(sessionReset: SessionReset): void {
        if (this.sessionModel.waiterMode) {
            this._currentStore = undefined;
            this._currentStoreLocation = undefined;
            this._singleStoreLocation = undefined;
            this.setOrderType(undefined);
            if (this.authService.isLoggedIn()) {
                this.authService.clearLocationContext();
            }
            this._preselectedTableNumber = undefined;
            this._preselectedTableNumberLabel = undefined;
            this._preselectedWebTerminalId = undefined;
            this._preselectedDealCode = undefined;
            this._exclusiveWebTerminalId = undefined;
            this._pickupOnlyMode = false;
            this._deliveryOnlyMode = false;
            this._tableOnlyMode = true;
        } else {
            if (!sessionReset.retainStore) {
                this._currentStore = undefined;
                this._currentStoreLocation = undefined;
                if (this.authService.isLoggedIn()) {
                    this.authService.clearLocationContext();
                }
            }
            if (!sessionReset.retainOrderType) {
                this.setOrderType(undefined);
            }
        }

        this._stores = undefined;
        this._singleStoreLocation = undefined;
        this._deliveryStores = undefined;

        this._deliveryInstructions = undefined;
        this._address = undefined;
        this._addressTrusted = false;
        this._deliveryInstructionsTrusted = false;
        this.expectedCompletion = undefined;
        this._saveAddressForMember = false;
        this._customerNotes = null;
        this._showMarketingOptIn = false;
        this._marketingOptIn = undefined;
        this._tipSelected = false;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public get parentAppURL(): string {
        return this._parentAppURL;
    }

    public get exclusiveWebTerminalId(): number {
        return this._exclusiveWebTerminalId;
    }

    public get exclusiveStoreGroup(): string {
        return this._exclusiveStoreGroup;
    }

    public get preselectedStoreGroup(): string {
        return this._preselectedStoreGroup;
    }

    public get exclusiveWebTerminalIdList(): number[] {
        return this._exclusiveWebTerminalIdList;
    }

    public get preselectedWebTerminalIdList(): number[] {
        return this._preselectedWebTerminalIdList;
    }

    public get preselectedTableNumber(): string {
        return this._preselectedTableNumber;
    }

    public get preselectedTableNumberLabel(): string {
        return this._preselectedTableNumberLabel;
    }

    public get preselectedTableSelection(): TableSelection {
        return this.preselectedTableNumber || this.preselectedStoreGroup || this.preselectedWebTerminalIdList ? {
            tableNumber: this.preselectedTableNumber,
            storeGroup: this.preselectedStoreGroup,
            webTerminalIds: this.preselectedWebTerminalIdList
        } : undefined;
    }

    public get currentTableNumberLabel(): string {
        return this._orderType !== OrderType.ORDER_AT_TABLE ? undefined
            : this._currentStore && this._currentStore.tableNumber != undefined ? this._currentStore.tableNumber
                : this.preselectedTableNumberLabel ? this.preselectedTableNumberLabel
                    : undefined;
    }

    public getAndClearPreselectedDealCode(): string {
        const code: string = this._preselectedDealCode;
        this._preselectedDealCode = undefined;
        return code;
    }

    public get tableOnlyMode(): boolean {
        return this._tableOnlyMode;
    }

    public get pickupOnlyMode(): boolean {
        return this._pickupOnlyMode;
    }

    public get deliveryOnlyMode(): boolean {
        return this._deliveryOnlyMode;
    }

    public get disableLocationServices(): boolean {
        return this._disableLocationServices;
    }

    public get stores(): StoreData[] {
        return this._stores;
    }

    public set stores(stores: StoreData[]) {
        this._stores = stores;
    }

    public get deliveryStores(): StoreData[] {
        return this._deliveryStores;
    }

    public set deliveryStores(value: StoreData[]) {
        this._deliveryStores = value;
    }

    public get currentStore(): StoreData {
        return this._currentStore;
    }

    public get menuId(): number {
        return this._currentStore ? this._currentStore.menuId
            : this.sessionModel.currentApp ? this.sessionModel.currentApp.defaultMenuId
                : undefined;
    }

    public get horizontalLogoImage(): string {
        return (this._currentStore && this._currentStore.horizontalLogoImage) || this.sessionModel.currentApp.horizontalLogoImage;
    }

    public get horizontalLogoImageSvg(): string {
        return (this._currentStore && this._currentStore.horizontalLogoImage) ? this._currentStore.horizontalLogoImageSvg : this.sessionModel.currentApp.horizontalLogoImageSvg;
    }

    public get horizontalLogoHeightPx(): number {
        return (this._currentStore && this._currentStore.horizontalLogoImage && this._currentStore.horizontalLogoHeightPx) || this.sessionModel.currentApp.horizontalLogoHeightPx;
    }

    public get verticalLogoImage(): string {
        return (this._currentStore && this._currentStore.verticalLogoImage) || this.sessionModel.currentApp.verticalLogoImage;
    }

    public get verticalLogoImageSvg(): string {
        return (this._currentStore && this._currentStore.verticalLogoImage) ? this._currentStore.verticalLogoImageSvg : this.sessionModel.currentApp.verticalLogoImageSvg;
    }

    public get verticalLogoWidthPx(): number {
        return (this._currentStore && this._currentStore.verticalLogoImage && this._currentStore.verticalLogoWidthPx) || this.sessionModel.currentApp.verticalLogoWidthPx;
    }

    public get singleOrderType(): OrderType {
        return this._singleOrderType;
    }

    public get orderType(): OrderType {
        return this._orderType;
    }

    public get singleStoreLocation(): StoreLocation {
        return this._singleStoreLocation;
    }

    public get currentStoreLocation(): StoreLocation {
        return this._currentStoreLocation;
    }

    /**
     * Returns the expectedCompletion date/time in a format suitable for interchange with the server (ISO8601 without timezone specifier).
     */
    public get expectedCompletionAsString(): string {
        if (!this.expectedCompletion) {
            return null;
        }
        return EntityData.formatLocalDateTime(this.expectedCompletion);
    }

    public getFormattedExpectedCompletion(includeYear: boolean = false, dateTime: Date = this.expectedCompletion): string {
        if (dateTime) {
            const timeSt = DateTime.formatTimeForDisplay(dateTime);
            const dateSt = DateTime.formatDateForDisplay(dateTime, includeYear);
            const todaySt = DateTime.formatDateForDisplay(this.sessionModel.now, includeYear);
            const dateString = dateSt === todaySt ? this.translate.instant("woa.label.ordering-context.today") : dateSt;
            return timeSt + " " + dateString;
        } else {
            return undefined;
        }
    }

    public get firstName(): string {
        return this._firstName;
    }

    public set firstName(value: string) {
        this._firstName = value;
    }

    public get trimmedFirstName(): string {
        return this._firstName ? this._firstName.trim() : undefined;
    }

    public get lastName(): string {
        return this._lastName;
    }

    public set lastName(value: string) {
        this._lastName = value;
    }

    public get trimmedLastName(): string {
        return this._lastName ? this._lastName.trim() : undefined;
    }

    public get email(): string {
        return this._email;
    }

    public set email(value: string) {
        this._email = value;
    }

    public get mobilePhone(): string {
        return this._mobilePhone;
    }

    public set mobilePhone(value: string) {
        this._mobilePhone = value;
    }

    public get deliveryInstructions(): string {
        return this._deliveryInstructions;
    }

    public set deliveryInstructions(value: string) {
        this._deliveryInstructions = value;
    }

    public get address(): AddressData {
        return this._address;
    }

    public set address(value: AddressData) {
        this._address = value;
    }

    public get firstNameTrusted(): boolean {
        return this._firstNameTrusted;
    }

    public set firstNameTrusted(value: boolean) {
        this._firstNameTrusted = value;
    }

    public get lastNameTrusted(): boolean {
        return this._lastNameTrusted;
    }

    public set lastNameTrusted(value: boolean) {
        this._lastNameTrusted = value;
    }

    public get emailTrusted(): boolean {
        return this._emailTrusted;
    }

    public set emailTrusted(value: boolean) {
        this._emailTrusted = value;
    }

    public get mobilePhoneTrusted(): boolean {
        return this._mobilePhoneTrusted;
    }

    public set mobilePhoneTrusted(value: boolean) {
        this._mobilePhoneTrusted = value;
    }

    // noinspection JSUnusedGlobalSymbols
    public get deliveryInstructionsTrusted(): boolean {
        return this._deliveryInstructionsTrusted;
    }

    public set deliveryInstructionsTrusted(value: boolean) {
        this._deliveryInstructionsTrusted = value;
    }

    public get addressTrusted(): boolean {
        return this._addressTrusted;
    }

    public set addressTrusted(value: boolean) {
        this._addressTrusted = value;
    }

    public get saveAddressForMember(): boolean {
        return this._saveAddressForMember;
    }

    public get customerNotes(): string {
        return this._customerNotes;
    }

    public set customerNotes(value: string) {
        this._customerNotes = value;
    }

    public get showMarketingOptIn(): boolean {
        return this._showMarketingOptIn;
    }

    public get marketingOptIn(): boolean {
        return this._marketingOptIn;
    }

    public set marketingOptIn(value: boolean) {
        if (this._marketingOptIn !== value) {
            this._marketingOptIn = value;
            OrderingContextEvents.marketingOptIn$.next(value);
        }
    }

    public get tipSelected(): boolean {
        return this._tipSelected;
    }

    public set tipSelected(value: boolean) {
        this._tipSelected = value;
    }

    /**
     * Determines whether we have an actual or effective single store case, as opposed to a single store-location.
     * Actual single store means that there is only one store defined.
     * Effective single store refers to an invalid configuration where a single store-location is defined,
     * but all stores support the same set of order types, thus rendering them indistinguishable from the
     * point of view of the app.
     */
    public get singleStore(): boolean {
        return !this._stores || (this.singleStoreLocation && this.singleStoreLocation.stores.length === 1);
    }

    public get hasStoreGroup(): boolean {
        return !!this._exclusiveStoreGroup || !!this._preselectedStoreGroup || !!this._exclusiveWebTerminalIdList || !!this._preselectedWebTerminalIdList;
    }

    public get canChangeStore(): boolean {
        return !this.singleStoreLocation && !(this._currentStore && this.orderType === OrderType.ORDER_AT_TABLE && !this.canChangeTableStore);
    }

    /**
     * Store selection can only be changed for a selected table when store groups are in used with a preselected table number.
     */
    public get canChangeTableStore(): boolean {
        return !!this.preselectedTableNumber && (!!this.preselectedWebTerminalIdList || !!this.preselectedStoreGroup);
    }

    public get isConfirmedTableOrdering(): boolean {
        return this._orderType === OrderType.ORDER_AT_TABLE && this._currentStore && !!this._currentStore.tableNumber;
    }

    public get isConfirmedDeliveryOrdering(): boolean {
        return this._orderType === OrderType.ORDER_DELIVERY && this._addressTrusted;
    }

    public get fulfilmentDetailsRequired(): boolean {
        return (this._orderType === OrderType.ORDER_AT_TABLE && !this._currentStore.tableNumber) || (this._orderType === OrderType.ORDER_DELIVERY && !this._addressTrusted);
    }

    public get canSelectOrderTime(): boolean {
        return this._orderType !== OrderType.ORDER_AT_TABLE && this._singleOrderType !== OrderType.ORDER_AT_TABLE &&
            (this._currentStore ? this._currentStore.orderAheadLaterTimeStore : (this.hasAllStoresWithOrderLaterTime() && !this.hasStoresInMultipleTimeZones()));
    }

    public get canShowAsapSelectionTime(): boolean {
        return this.sessionModel.currentApp.showAsapSelectionTime || (this._currentStore && !this._currentStore.orderAheadLaterTimeStore);
    }

    public get canShowETA(): boolean {
        return this.sessionModel.currentApp.showAsapSelectionTime || (this._currentStore ? this._currentStore.orderAheadLaterTimeStore : this.hasAllStoresWithOrderLaterTime());
    }

    public canShowETAForStore(store: StoreData): boolean {
        return this.sessionModel.currentApp.showAsapSelectionTime || store.orderAheadLaterTimeStore;
    }

    public hasAnyStoresWithOrderLaterTime(stores: StoreData[] = this._stores): boolean {
        return !!stores && stores.some((store) => store.orderAheadLaterTimeStore);
    }

    public hasAllStoresWithOrderLaterTime(stores: StoreData[] = this._stores): boolean {
        return !!stores && stores.every((store) => store.orderAheadLaterTimeStore);
    }

    public get integratedDelivery(): boolean {
        return this.orderType === OrderType.ORDER_DELIVERY && this._currentStore && this._currentStore.integratedDelivery;
    }

    public hasAnyStoresWithOrderLater(stores: StoreData[] = this._stores): boolean {
        return !!stores && stores.find((store) => store.orderAheadLaterStore) != undefined;
    }

    public hasAnyStoresWithState(stores: StoreData[] = this._stores): boolean {
        return !!stores && stores.find((store) => !!store.state) != undefined;
    }

    public hasStoresInMultipleTimeZones(): boolean {
        if (!this._stores) {
            return false;
        }
        let storeTimeZones: string[] = this._stores.map((store: StoreData) => store.timeZone);
        storeTimeZones = storeTimeZones.filter((timeZone: string, pos: number) => storeTimeZones.indexOf(timeZone) === pos);
        return storeTimeZones.length > 1;
    }

    public get showWaitTimeNotices(): boolean {
        return this.orderType === OrderType.ORDER_AT_TABLE || !this.expectedCompletion;
    }

    public get showVenueWaitTimeNotice(): boolean {
        return this._currentStore && !this._currentStore.closed && !!this.venueWaitTime && this.showWaitTimeNotices;
    }

    public get venueWaitTime(): number {
        return !this._currentStore || (this._currentStore.currentWaitTimeMinutes || 0) <= (this._currentStore.completionTime || 0) ? 0 : this._currentStore.currentWaitTimeMinutes;
    }

    public get headerImage(): FedeltaImageData {
        return this.currentStoreHasImage ? this._currentStore.image
            : this.sessionModel.currentApp && this.sessionModel.currentApp.orderingImage && this.sessionModel.currentApp.orderingImage.id ? this.sessionModel.currentApp.orderingImage
                : undefined;
    }

    public get headerImageOpacity(): number {
        return this.currentStoreHasImage ? this._currentStore.imageOpacity
            : this.sessionModel.currentApp ? this.sessionModel.currentApp.orderingImageOpacity
                : 1.0;
    }

    public get hasHeaderImage(): boolean {
        return this.currentStoreHasImage || !!(this.sessionModel.currentApp && this.sessionModel.currentApp.orderingImage && (this.sessionModel.currentApp.orderingImage.id || this.sessionModel.currentApp.orderingImage.image));
    }

    public get currentStoreHasImage(): boolean {
        return !!(this._currentStore && this._currentStore.image && (this._currentStore.image.id || this._currentStore.image.image));
    }

    public get showPhoneNumber(): boolean {
        return this._orderType === OrderType.ORDER_AHEAD ? this.sessionModel.currentApp.showPhoneNumberForPickup
            : this._orderType === OrderType.ORDER_DELIVERY ? this.sessionModel.currentApp.showPhoneNumberForDelivery || (this._currentStore && this._currentStore.integratedDelivery)
                : this._orderType === OrderType.ORDER_AT_TABLE ? this.sessionModel.currentApp.showPhoneNumberForTable
                    : false;
    }

    public get showEmail(): boolean {
        return this._orderType === OrderType.ORDER_AHEAD ? this.sessionModel.currentApp.showEmailForPickup
            : this._orderType === OrderType.ORDER_DELIVERY ? this.sessionModel.currentApp.showEmailForDelivery || (this._currentStore && this._currentStore.integratedDelivery)
                : this._orderType === OrderType.ORDER_AT_TABLE ? this.sessionModel.currentApp.showEmailForTable
                    : false;
    }

    public get showName(): boolean {
        return this._orderType === OrderType.ORDER_AHEAD ? this.sessionModel.currentApp.showNameForPickup
            : this._orderType === OrderType.ORDER_DELIVERY ? this.sessionModel.currentApp.showNameForDelivery || (this._currentStore && this._currentStore.integratedDelivery)
                : this._orderType === OrderType.ORDER_AT_TABLE ? this.sessionModel.currentApp.showNameForTable
                    : false;
    }

    public get requirePhoneNumber(): boolean {
        return !this.showPhoneNumber ? false
            : this._orderType === OrderType.ORDER_AHEAD ? this.sessionModel.currentApp.requirePhoneNumberForPickup
                : this._orderType === OrderType.ORDER_DELIVERY ? this.sessionModel.currentApp.requirePhoneNumberForDelivery || (this._currentStore && this._currentStore.integratedDelivery)
                    : this._orderType === OrderType.ORDER_AT_TABLE ? this.sessionModel.currentApp.requirePhoneNumberForTable
                        : false;
    }

    public get requireEmail(): boolean {
        return !this.showEmail ? false
            : this._orderType === OrderType.ORDER_AHEAD ? this.sessionModel.currentApp.requireEmailForPickup
                : this._orderType === OrderType.ORDER_DELIVERY ? this.sessionModel.currentApp.requireEmailForDelivery || (this._currentStore && this._currentStore.integratedDelivery)
                    : this._orderType === OrderType.ORDER_AT_TABLE ? this.sessionModel.currentApp.requireEmailForTable
                        : false;
    }

    public get requireFirstName(): boolean {
        return !this.showName ? false
            : this._orderType === OrderType.ORDER_AHEAD ? this.sessionModel.currentApp.requireFirstNameForPickup
                : this._orderType === OrderType.ORDER_DELIVERY ? this.sessionModel.currentApp.requireFirstNameForDelivery || (this._currentStore && this._currentStore.integratedDelivery)
                    : this._orderType === OrderType.ORDER_AT_TABLE ? this.sessionModel.currentApp.requireFirstNameForTable
                        : false;
    }

    public get requireLastName(): boolean {
        return !this.showName ? false
            : this._orderType === OrderType.ORDER_DELIVERY ? this._currentStore.integratedDelivery
                : false;
    }

    public async loadStores(force: boolean = false): Promise<void> {
        if (this._stores && !force) {
            return;
        }

        try {
            this.storeSearchModel.filter[StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME] = this.expectedCompletionAsString;
            await this.storeSearchModel.retrieve();
        } catch (error) {
            console.log(error);
            return;
        }

        this._stores = this.storeSearchModel.data;
        this._singleStoreLocation = undefined;
        this._singleOrderType = undefined;
        this.storeSearchModel.determineAvailableStores();

        // Determine if there is a single store-location.
        let locations: string[] = this._stores.map((store: StoreData) => store.locationName);
        locations = locations.filter((locationName: string, pos: number) => locations.indexOf(locationName) === pos);
        if (locations.length === 1) {
            let storeNames: string[] = this._stores.map((store: StoreData) => store.name);
            storeNames = storeNames.filter((storeName: string, pos: number) => storeNames.indexOf(storeName) === pos);
            if (storeNames.length === 1) {

                // It is not considered valid to define multiple stores with the same name at the same location for a given order type.
                // We discard any store for which all order types are provided by a store earlier in the list.
                // This allows easier detection of the effective single store case where multiple stores are defined for a store-location,
                // but they are indistinguishable.
                // Note that this does not guarantee that no two stores provide the same order type, but rather that each store
                // provides at least one unique order type. The getStoreForOrderType of StoreLocation selects the first store for a given order type.
                // Note also that for multi-store, this filtering of stores does not currently occur, as it is more about ensuring
                // proper handling of single stores.

                const distinctStores: StoreData[] = this._stores.filter((store: StoreData, pos: number) =>
                    (store.orderAheadStore && this._stores.findIndex((otherStore: StoreData) => otherStore.orderAheadStore) === pos) ||
                    (store.orderDelivery && this._stores.findIndex((otherStore: StoreData) => otherStore.orderDelivery) === pos) ||
                    (store.orderAtTableStore && this._stores.findIndex((otherStore: StoreData) => otherStore.orderAtTableStore) === pos));
                this._singleStoreLocation = new StoreLocation(this._stores[0].name, locations[0], distinctStores);
            }
        }

        let selectedStore: StoreData = undefined;

        // Reselect the current store if any
        if (this._currentStore) {
            selectedStore = this._stores.find((store) => store.webTerminalId === this._currentStore.webTerminalId);
        } else {
            // Auto-select the preselected order type, store, and optionally table
            if (this._preselectedWebTerminalId) {
                selectedStore = this.stores.find((store) => store.webTerminalId === this._preselectedWebTerminalId);
                if (selectedStore) {
                    selectedStore = await this.applyPreselectedTableNumberToSelectedStore(selectedStore);
                }
            } else if (this._preselectedTableNumber && !this._pickupOnlyMode && !this._deliveryOnlyMode) {
                this.setOrderType(OrderType.ORDER_AT_TABLE);

                // Even though there are multiple stores, and any custom table number could theoretically be different for each one,
                // we assume for now that they are likely to be the same and that any mapping is better than none.
                // Once a store is selected, the custom lookup will be done again to make sure the mapping is correct.
                const lookupStore: StoreData = await this.lookupCustomTableNumber(this._preselectedTableNumber, this._preselectedStoreGroup, this.stores[0].webTerminalId);
                if (lookupStore && lookupStore.tableNumber) {
                    this._preselectedTableNumberLabel = lookupStore.tableNumber;
                } else if (!this._preselectedTableNumberLabel) {
                    this._preselectedTableNumberLabel = await this.getDefaultTableName(this._preselectedTableNumber);
                }
            }

            // Auto-select an actual single store
            if (!selectedStore && this.singleStore) {
                selectedStore = await this.applyPreselectedTableNumberToSelectedStore(this._stores[0]);
            }

            // If the auto-selected store has a single order type, auto-select that also.
            if (selectedStore && !this._orderType) {
                if (selectedStore.orderAheadStore && !selectedStore.orderDelivery && !selectedStore.orderAtTableStore) {
                    this.setOrderType(OrderType.ORDER_AHEAD);
                } else if (selectedStore.orderDelivery && !selectedStore.orderAheadStore && !selectedStore.orderAtTableStore) {
                    this.setOrderType(OrderType.ORDER_DELIVERY);
                } else if (selectedStore.orderAtTableStore && !selectedStore.orderAheadStore && !selectedStore.orderDelivery) {
                    this.setOrderType(OrderType.ORDER_AT_TABLE);
                } else if ((this._orderType === OrderType.ORDER_AHEAD && !selectedStore.orderAheadStore) ||
                    (this._orderType === OrderType.ORDER_DELIVERY && !selectedStore.orderDelivery) ||
                    (this._orderType === OrderType.ORDER_AT_TABLE && !selectedStore.orderAtTableStore)) {
                    this.setOrderType(undefined);
                }
            }
        }

        // If we have a single store-location, and we know the order type, we can auto-select the store
        if (!selectedStore && this._singleStoreLocation && this._orderType) {
            selectedStore = this._singleStoreLocation.getStoreForOrderType(this._orderType);
        }

        // Retain the selected table code if the store is unchanged
        if (selectedStore && this._currentStore && this._currentStore.webTerminalId === selectedStore.webTerminalId && this._currentStore.tableNumber) {
            const store: StoreData = <StoreData>EntityData.create(StoreData).assign(selectedStore);
            store.tableNumber = this._currentStore.tableNumber;
            if (await this.routeToExternalStore(store, OrderType.ORDER_AT_TABLE)) {
                return;
            }
            this._currentStore = store;
            this.setOrderType(OrderType.ORDER_AT_TABLE);
            this.updateCurrentStoreLocation();
        } else {
            if (selectedStore && await this.routeToExternalStore(selectedStore, this._orderType)) {
                return;
            }
            await this.setCurrentStore(selectedStore, this._orderType);
        }

        // Set the order type if there is only one option
        if (!this.storeSearchModel.hasMultipleStoreTypes$.value) {
            if (this.storeSearchModel.hasOrderAheadStores$.value) {
                this._singleOrderType = OrderType.ORDER_AHEAD;
            } else if (this.storeSearchModel.hasOrderDeliveryStores$.value) {
                this._singleOrderType = OrderType.ORDER_DELIVERY;
            } else if (this.storeSearchModel.hasOrderAtTableStores$.value) {
                this._singleOrderType = OrderType.ORDER_AT_TABLE;
            }
        }
        if (!this._orderType) {
            this.setOrderType(this._singleOrderType);
        }

        if (!this._disableLocationServices && !this._currentStore) {
            // noinspection ES6MissingAwait
            this.loadNearestStores();
        }
    }

    private async loadNearestStores(): Promise<void> {
        const position: Position = await this.geolocateService.getCurrentLocation();

        // The app may be cleared during current location retrieval - do not attempt loading of the nearest stores in that case.
        if (this.sessionModel.currentApp) {
            try {
                await this.storeSearchModel.loadNearestStores(this.sessionModel.currentApp.storeLimit, this.sessionModel.currentApp.storeRange, this.sessionModel.currentApp.deliveryStoreRange, position.coords.latitude, position.coords.longitude);
            } catch (error) {
                console.log("Unable to load nearest stores", error);
            }
        }
    }

    /**
     * Nearest stores for a delivery address are cached in the session model
     * to avoid the repeated overhead and cost of distance by road calculation.
     *
     * @param storeSearchModel
     * @param deliveryDetails
     * @returns an error message key if the specified delivery details are invalid for delivery, undefined otherwise.
     */
    public async findDeliveryStores(storeSearchModel: StoreSearchModel, deliveryDetails: DeliveryDetails): Promise<string> {
        if (!(deliveryDetails.latitude && deliveryDetails.longitude)) {
            this._deliveryStores = [];
            return undefined;
        }
        return new Promise<string>((resolve) => {

            // Note that we don't use the delivery store limit here if using distance by road, because a store which is closer radially may be further away by road.
            // The assumption is that the general limit is higher, and will pick up more stores, which can be further refined below.
            const storeLimit: number = this.sessionModel.currentApp.deliveryDistanceByRoad && this.sessionModel.currentApp.storeLimit ? this.sessionModel.currentApp.storeLimit : this.sessionModel.currentApp.deliveryStoreLimit;
            console.log("Nearest store search:", storeLimit, deliveryDetails.latitude, deliveryDetails.longitude);
            storeSearchModel.findNearestStores(storeLimit,
                this.sessionModel.currentApp.storeSearchRange, this.sessionModel.currentApp.deliveryStoreRange,
                deliveryDetails.latitude, deliveryDetails.longitude, true);
            if (storeSearchModel.nearestStores.length == 0) {
                console.log("No nearest stores found");
                this._deliveryStores = [];
                resolve("woa.message.ordering-context.invalidDeliveryAddress");
            } else if (this.sessionModel.currentApp.deliveryDistanceByRoad) {
                const origin: LatLng = new LatLng(deliveryDetails.latitude, deliveryDetails.longitude);
                const destinations: LatLng[] = storeSearchModel.nearestStores.map((store) => new LatLng(store.latitude, store.longitude));
                const request: DistanceMatrixRequest = {
                    travelMode: TravelMode.DRIVING,
                    origins: [origin],
                    destinations: destinations
                };
                console.log("Delivery origin:", origin);
                console.log("Delivery destinations:", destinations);
                console.log("Delivery distance lookup request:", request);
                this.distanceMatrixService.getDistanceMatrix(request, (response, status) => {
                    console.log("Delivery distance lookup result:", status, response);
                    if (status === DistanceMatrixStatus.OK) {
                        this._deliveryStores = StoreSearchModel.applyNearestStoreLimit(this.sessionModel.currentApp.deliveryStoreLimit,
                            storeSearchModel.nearestStores
                                .map((store: StoreData) => <StoreData>EntityData.create(StoreData).assign(store))
                                .filter((store: StoreData, i: number) => {
                                    const distanceMatrixResponseElement: DistanceMatrixResponseElement = response.rows[0].elements[i];
                                    // keep the radius if the distance by road could not be calculated
                                    if (distanceMatrixResponseElement.status === DistanceMatrixElementStatus.OK) {
                                        store.distance = Math.round(distanceMatrixResponseElement.distance.value / 100) / 10;
                                    }
                                    return !this.sessionModel.currentApp.storeSearchRange || store.distance <= this.sessionModel.currentApp.deliveryStoreRange;
                                }));
                    } else {
                        this._deliveryStores = [];
                    }
                    if (this._deliveryStores.length == 0) {
                        resolve("woa.message.ordering-context.invalidDeliveryAddress");
                    }
                    resolve(undefined);
                });
            } else {
                this._deliveryStores = StoreSearchModel.applyNearestStoreLimit(this.sessionModel.currentApp.deliveryStoreLimit,
                    storeSearchModel.nearestStores
                        .map((store: StoreData) => <StoreData>EntityData.create(StoreData).assign(store))
                        .filter((store: StoreData) => !this.sessionModel.currentApp.storeSearchRange || store.distance <= this.sessionModel.currentApp.deliveryStoreRange));
                if (this._deliveryStores.length == 0) {
                    resolve("woa.message.ordering-context.invalidDeliveryAddress");
                }
                resolve(undefined);
            }
        });
    }

    public async validateAndReapplyCurrentStore(): Promise<boolean> {
        if (!this._currentStore) {
            return true;
        }

        await this.storeSearchModel.retrieve();
        if (!this.storeSearchModel.data.some((store: StoreData) => store.webTerminalId === this._currentStore.webTerminalId)) {
            if (OrderEvents.orderHasItems$.value) {
                await this.notificationService.displayAlert(undefined, "woa.message.identification.memberNotAvailableOrderedStore");
            }
            this._currentStore = null;
            this._orderModel.reset("store invalid");
            return false;
        }

        this.authService.setLocationContext(this._currentStore.locationId, this._currentStore.outletId, this._currentStore.webTerminalId);
        return true;
    }

    public async lookupCustomTableNumber(lookupCode: string, storeGroup?: string, webTerminalId?: number, ignoreExclusiveFilters: boolean = true, returnFirstMatch = false): Promise<StoreData> {
        try {
            const searchResult: SearchResult = await this.storeSearchModel.searchRecords({
                [OrderingContextStoreSearchFilterProvider.FILTER_IGNORE_EXCLUSIVE]: ignoreExclusiveFilters,
                [StoreSearchModel.FILTER_LOOKUP_CODE]: lookupCode,
                [StoreSearchModel.FILTER_STORE_GROUP]: storeGroup,
                [StoreSearchModel.FILTER_ORDER_LOCAL_DATE_TIME]: this.expectedCompletionAsString
            }, undefined, undefined, undefined).toPromise();
            const matches: StoreData[] = webTerminalId ? searchResult.data.filter((store: StoreData) => store.webTerminalId === webTerminalId) : searchResult.data;
            return matches.length === 1 || (matches.length > 1 && returnFirstMatch) ? matches[0] : undefined;
        } catch (error) {
            console.log("Custom table number lookup failed: ", error);
        }
        return undefined;
    }

    private async setCurrentStore(store: StoreData, orderType: OrderType): Promise<void> {
        if (this._currentStore !== store) {
            const storeChanged: boolean = ((store == undefined) !== (this._currentStore == undefined)) ||
                (store && this._currentStore && store.webTerminalId !== this._currentStore.webTerminalId);
            let tableChanged: boolean = store && this._currentStore && store.tableNumber !== this._currentStore.tableNumber;
            this._currentStore = store;

            if (store) {
                if (orderType !== OrderType.ORDER_AT_TABLE) {
                    store.tableNumber = undefined;
                } else if (store.tableNumber) {
                    this._preselectedTableNumber = store.tableNumber;
                    this._preselectedTableNumberLabel = store.tableNumber;
                } else if (this._preselectedTableNumber) {
                    await this.applyTableNumberToStore(this._preselectedTableNumber, store);
                    this._preselectedTableNumberLabel = store.tableNumber;
                    tableChanged = true;
                }

                if (!store.orderAheadLaterTimeStore) {
                    this.expectedCompletion = undefined;
                }
            }

            if (storeChanged) {
                if (store) {
                    this.authService.setLocationContext(store.locationId, store.outletId, store.webTerminalId);
                } else {
                    this.authService.clearLocationContext();
                }

                this.updateCurrentStoreLocation();

                // TODO: The following will need to be improved to allow non-destructive change of store when possible
                this._orderModel.invalidateOrder();
            }

            if (storeChanged || tableChanged) {
                OrderingContextEvents.storeDetailsChanged$.next(storeChanged);
                return this.analyticsService.trackSetStore();
            }
        }
    }

    private async routeToExternalStore(store: StoreData, orderType: OrderType): Promise<boolean> {
        if (!this.responsiveService.isApp && store && (store.parentAppStore || (store.externalAppURL && store.externalAppWebTerminalId))) {
            this._currentStore = undefined;
            this.navigationService.redirectToUrl(await this.paramConfigService.getURLForExternalStore(store, orderType));
            return true;
        }
        return false;
    }

    public async routeToParentApp(): Promise<void> {
        if (!this.responsiveService.isApp && this._parentAppURL) {
            this._currentStore = undefined;
            this.navigationService.redirectToUrl(await this.paramConfigService.getURLForParentApp());
        }
    }

    private setOrderType(orderType: OrderType): boolean {
        if (this._orderType === orderType) {
            return false;
        }
        this._orderType = orderType;
        OrderingContextEvents.orderType$.next(orderType);
        return true;
    }

    public async applyOrderType(orderType: OrderType): Promise<any> {
        if (!this.setOrderType(orderType)) {
            return;
        }

        // If we have a single store location, we can auto-select the store
        // TODO - this will currently invalidate the order if the store changes
        if (orderType && this._singleStoreLocation && (!this._currentStore || !this._currentStore.canOrder(orderType))) {
            await this.setCurrentStore(this._singleStoreLocation.getStoreForOrderType(this._orderType), orderType);
        }

        // If switching to table order, apply the preselected table number to the selected store, otherwise remove the table number from the selected store.
        if (this._currentStore) {
            if (orderType === OrderType.ORDER_AT_TABLE && this._preselectedTableNumber) {
                await this.applyTableNumberToStore(this._preselectedTableNumber, this._currentStore);
            } else if (orderType !== OrderType.ORDER_AT_TABLE) {
                this._currentStore.tableNumber = undefined;
            }
        }

        // Remove an entitlement that is not available for this order type
        this._dealModel.removeUnavailableEntitlement();

        // Remove order items that are not available for this order type
        return this._orderModel.removeUnavailableOrderItems({orderType: orderType});
    }

    /**
     * Applies the specified table number to the specified store.
     * Looks for a possible custom table number mapping against the store for the table number.
     * @param tableNumber
     * @param store
     */
    private async applyTableNumberToStore(tableNumber: string, store: StoreData) {
        const lookupStore: StoreData = await this.lookupCustomTableNumber(tableNumber, undefined, store.webTerminalId);
        if (lookupStore && lookupStore.tableNumber) {
            store.tableNumber = lookupStore.tableNumber;
        } else {
            store.tableNumber = await this.getDefaultTableName(tableNumber);
        }
    }

    public async getDefaultTableName(tableNumber: string): Promise<string> {
        tableNumber = tableNumber.trim();
        if (tableNumber.indexOf(" ") == -1 && !this.sessionModel.currentApp.appSettings.find((setting: AppSettingData) => setting.name === "disableDefaultTablePrefix" && setting.value === "true")) {
            tableNumber = await this.translate.get("woa.common.order.atTable.placement.noun.uc").toPromise() + " " + tableNumber;
        }
        return tableNumber.trim();
    }

    /**
     * Set the current details based on the provided ordering context.
     * @param orderingContext
     */
    public async applyOrderingContext(orderingContext: OrderingContext): Promise<boolean> {
        if (!orderingContext) {
            return true;
        }

        const storeChanged: boolean = orderingContext.selectedStore && (!this._currentStore || orderingContext.selectedStore.webTerminalId !== this._currentStore.webTerminalId);
        const storeOrTableChanged: boolean = storeChanged ||
            (orderingContext.selectedStore && this._currentStore && orderingContext.selectedStore.tableNumber !== this._currentStore.tableNumber) ||
            (orderingContext.selectedTable && !!orderingContext.selectedTable.lookupCode) ||
            (orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE && !this._currentStore.tableNumber && !!this._preselectedTableNumber);

        // If the store changes, the current order will be cleared.
        // Give the user the chance to opt-out
        if (storeChanged) {
            if (OrderEvents.orderHasItems$.value &&
                !await this.notificationService.displayConfirmation("woa.heading.order.confirmRestartOrder", undefined,
                    "woa.message.order.confirmRestartOrder",
                    "woa.button.common.noThanks",
                    "woa.button.order.restartOrder",
                    true)) {
                return false;
            }
            if (await this.routeToExternalStore(orderingContext.selectedStore, orderingContext.selectedOrderType)) {
                return true;
            }
        }

        // No store change or no order to clear
        if (orderingContext.selectedOrderType != undefined) {
            const confirmed: boolean = await this.confirmOrderTypeChange(orderingContext.selectedOrderType);
            if (!confirmed) {
                return false;
            }
        }

        // Apply selected delivery details.
        if (orderingContext.selectedDeliveryDetails) {
            this.applyDeliveryDetails(orderingContext.selectedDeliveryDetails);
        }

        // Apply details of the selected table.
        // These will replace any pre-selections provided via URL parameters, and may lock down certain aspects of the UI.
        const prevExclusiveWebTerminalId: number = this._exclusiveWebTerminalId;
        const prevExclusiveWebTerminalIdListSt: string = this._exclusiveWebTerminalIdList ? this._exclusiveWebTerminalIdList.join(",") : "";
        const prevTableOnlyMode: boolean = this._tableOnlyMode;
        const prevPickupOnlyMode: boolean = this._pickupOnlyMode;
        const prevDeliveryOnlyMode: boolean = this._deliveryOnlyMode;
        let tableNumberSet: boolean = false;
        let tableNumberLabelSet: boolean = false;
        let webTerminalsSet: boolean = false;
        let tableOnlyModeSet: boolean = false;
        let pickupOnlyModeSet: boolean = false;
        let deliveryOnlyModeSet: boolean = false;
        if (orderingContext.selectedTable) {
            if (orderingContext.selectedTable.lookupCode) {
                this._preselectedTableNumber = orderingContext.selectedTable.lookupCode;
                tableNumberSet = true;
            }
            if (orderingContext.selectedTable.tableNumber) {
                this._preselectedTableNumberLabel = orderingContext.selectedTable.tableNumber;
                tableNumberLabelSet = true;
            }
            if (orderingContext.selectedTable.webTerminalIds || orderingContext.selectedTable.storeGroup) {
                this._preselectedWebTerminalId = undefined;
                this._exclusiveWebTerminalId = undefined;
                this._preselectedStoreGroup = undefined;
                this._exclusiveStoreGroup = undefined;
                this._preselectedWebTerminalIdList = undefined;
                this._exclusiveWebTerminalIdList = undefined;
                if (orderingContext.selectedTable.storeGroup) {
                    this._preselectedStoreGroup = orderingContext.selectedTable.storeGroup;
                    this._exclusiveStoreGroup = orderingContext.selectedTable.exclusiveStores ? this._preselectedStoreGroup : undefined;
                } else if (orderingContext.selectedTable.webTerminalIds && orderingContext.selectedTable.webTerminalIds.length > 0) {
                    if (orderingContext.selectedTable.webTerminalIds.length === 1) {
                        this._preselectedWebTerminalId = orderingContext.selectedTable.webTerminalIds[0];
                        this._exclusiveWebTerminalId = orderingContext.selectedTable.exclusiveStores ? this._preselectedWebTerminalId : undefined;
                    } else {
                        this._preselectedWebTerminalIdList = orderingContext.selectedTable.webTerminalIds;
                        this._exclusiveWebTerminalIdList = orderingContext.selectedTable.exclusiveStores ? this._preselectedWebTerminalIdList : undefined;
                    }
                }
                webTerminalsSet = true;
            }
            if (orderingContext.selectedTable.tableOnlyMode != undefined) {
                this._tableOnlyMode = orderingContext.selectedTable.tableOnlyMode;
                tableOnlyModeSet = true;
            }
            if (orderingContext.selectedTable.pickupOnlyMode != undefined) {
                this._pickupOnlyMode = orderingContext.selectedTable.pickupOnlyMode;
                pickupOnlyModeSet = true;
            }
            if (orderingContext.selectedTable.deliveryOnlyMode != undefined) {
                this._deliveryOnlyMode = orderingContext.selectedTable.deliveryOnlyMode;
                deliveryOnlyModeSet = true;
            }
        }
        const paramConfig: ParamConfig = await this.paramConfigService.getConfig();
        this.applyOrderingContextToParamConfig(orderingContext, paramConfig);
        if (orderingContext.selectedStore && orderingContext.selectedOrderType === OrderType.ORDER_AT_TABLE && orderingContext.selectedStore.tableNumber) {
            if (!tableNumberSet) {
                this._preselectedTableNumber = undefined;
            }
            if (!tableNumberLabelSet) {
                this._preselectedTableNumberLabel = undefined;
            }
            if (!webTerminalsSet) {
                this._preselectedWebTerminalId = undefined;
                this._exclusiveWebTerminalId = undefined;
                this._preselectedStoreGroup = undefined;
                this._exclusiveStoreGroup = undefined;
                this._preselectedWebTerminalIdList = undefined;
                this._exclusiveWebTerminalIdList = undefined;
            }
            if (!tableOnlyModeSet) {
                this._tableOnlyMode = paramConfig.tableOnlyMode;
            }
            if (!pickupOnlyModeSet) {
                this._pickupOnlyMode = paramConfig.pickupOnlyMode;
            }
            if (!deliveryOnlyModeSet) {
                this._deliveryOnlyMode = paramConfig.deliveryOnlyMode;
            }
        }
        if (this._exclusiveWebTerminalId !== prevExclusiveWebTerminalId ||
            (this._exclusiveWebTerminalIdList ? this._exclusiveWebTerminalIdList.join(",") : "") !== prevExclusiveWebTerminalIdListSt ||
            this._tableOnlyMode !== prevTableOnlyMode ||
            this._pickupOnlyMode !== prevPickupOnlyMode ||
            this._deliveryOnlyMode !== prevDeliveryOnlyMode) {
            await this.loadStores(true);
        }

        if (storeOrTableChanged) {
            await this.setCurrentStore(orderingContext.selectedStore, orderingContext.selectedOrderType);
        }

        // Changing the order type will remove any products which are no longer available
        if (orderingContext.selectedOrderType != undefined) {
            await this.applyOrderType(orderingContext.selectedOrderType);
        }

        // The store hasn't changed, but we will reload the schedules, and remove any products which are no longer available
        if (!storeChanged && this.activeSchedulesSearchModel) {
            await this.refreshActiveSchedules();
        }

        // Initiate ordering of a selected product.
        if (orderingContext.selectedMenuItem) {
            // noinspection ES6MissingAwait
            this._menuModel.orderItemWhenMenuLoaded(orderingContext.selectedMenuItem);
        }

        return true;
    }

    public applyDeliveryDetails(deliveryDetails: DeliveryDetails): void {
        this._mobilePhone = deliveryDetails.orderMobilePhone;
        this._deliveryInstructions = deliveryDetails.deliveryInstructions;
        this._address = deliveryDetails.address;
        this._saveAddressForMember = deliveryDetails.saveAddressForMember && this._identityModel.currentMember.allowProfileEdit;

        this._mobilePhoneTrusted = true;
        this._deliveryInstructionsTrusted = true;
        this._addressTrusted = true;
        OrderingContextEvents.deliveryDetailsChanged$.next();
    }

    /**
     * Apply the saved customer contact details to the ordering context.
     *
     * @param force If true, all details are applied whether present in the saved details or not, and whether already trusted or not.
     * This is intended for the case where the user is directly editing their contact details. Details not present are cleared and marked as untrusted,
     * so that they can be defaulted back to member contact details, which supports clearing individual fields.
     * If false, only present details which are not already trusted are applied. This is intended for use when a member
     * has logged in, and we want to retain any trusted details the user has already supplied.
     */
    public applySavedCustomerDetails(force: boolean): CustomerDetails {
        const customerDetails: CustomerDetails = this.customerDetailsService.loadCustomer();

        if (force || (!this._firstNameTrusted && !this._lastNameTrusted && (customerDetails.firstName || customerDetails.lastName))) {
            this._firstName = customerDetails.firstName;
            this._lastName = customerDetails.lastName;
            this._firstNameTrusted = !!(customerDetails.firstName || customerDetails.lastName);
            this._lastNameTrusted = !!(customerDetails.firstName || customerDetails.lastName);
        }
        if (force || (!this._emailTrusted && customerDetails.email)) {
            this._email = customerDetails.email;
            this._emailTrusted = !!customerDetails.email;
        }
        if (force || (!this.mobilePhoneTrusted && customerDetails.mobilePhone)) {
            this._mobilePhone = customerDetails.mobilePhone;
            this._mobilePhoneTrusted = !!customerDetails.mobilePhone;
        }

        return customerDetails;
    }

    /**
     * Apply the contact details from the saved customer details, and maybe from the logged-in identity, to the ordering context.
     * Details from the logged-in identity are not "trusted" to be saved for future use on the device,
     * and will not override previously saved or recently entered contact details for use on the order.
     * As such, individual details are only applied if they are flagged as untrusted
     * (i.e. if they are unset or obtained only from a previously logged-in member's details, rather than from the user).
     */
    public applyIdentityContactDetails(currentIdentity: IdentityData, customerDetails?: CustomerDetails): void {

        // Reload the saved customer details if not provided (provided details are assumed to have been saved).
        // We keep any details we have which are already trusted (member login shouldn't cause already entered details to be lost).
        if (!customerDetails) {
            customerDetails = this.applySavedCustomerDetails(false);
        }

        // Apply untrusted identity details where that's all we have
        if (!this._firstNameTrusted && !this._lastNameTrusted) {
            this._firstName = currentIdentity.firstName;
            this._lastName = currentIdentity.lastName;
        }
        if (!this._emailTrusted) {
            this._email = currentIdentity.email;
        }
        if (!this._mobilePhoneTrusted) {
            this._mobilePhone = currentIdentity.mobilePhone;
        }
        if (!this._deliveryInstructionsTrusted) {
            this._deliveryInstructions = currentIdentity.deliveryInstructions;
        }
        if (!this._addressTrusted) {
            this._address = <AddressData>EntityData.create(AddressData).assign(currentIdentity.homeAddress);
        }

        if (!customerDetails.marketingOptInDecided && !this.sessionModel.waiterMode) {
            this._showMarketingOptIn = this.sessionModel.currentApp.showMarketingOptIn;
            if (this._showMarketingOptIn && this._marketingOptIn == undefined) {
                this._marketingOptIn = true;
            }
        }
    }

    /**
     * Ensure the param config matches the ordering context for redirecting to another app
     *
     * @param orderingContext
     * @param paramConfig
     */
    private applyOrderingContextToParamConfig(orderingContext: OrderingContext, paramConfig: ParamConfig): void {
        if (orderingContext.selectedTable) {
            if (orderingContext.selectedTable.lookupCode) {
                paramConfig.tableNumber = orderingContext.selectedTable.lookupCode;
            }
            if (orderingContext.selectedTable.storeGroup) {
                paramConfig.storeGroup = orderingContext.selectedTable.storeGroup;
                paramConfig.exclusiveStoreGroup = orderingContext.selectedTable.exclusiveStores ? this._preselectedStoreGroup : undefined;
            }
            if (orderingContext.selectedTable.webTerminalIds && orderingContext.selectedTable.webTerminalIds.length > 0) {
                if (orderingContext.selectedTable.webTerminalIds.length === 1) {
                    paramConfig.store = orderingContext.selectedTable.webTerminalIds[0];
                    paramConfig.exclusiveStore = orderingContext.selectedTable.exclusiveStores ? this._preselectedWebTerminalId : undefined;
                } else {
                    paramConfig.stores = orderingContext.selectedTable.webTerminalIds;
                    paramConfig.exclusiveStores = orderingContext.selectedTable.exclusiveStores ? this._preselectedWebTerminalIdList : undefined;
                }
            }
            if (!orderingContext.selectedTable.localTable) {
                if (orderingContext.selectedTable.tableOnlyMode != undefined) {
                    paramConfig.tableOnlyMode = orderingContext.selectedTable.tableOnlyMode;
                }
                if (orderingContext.selectedTable.pickupOnlyMode != undefined) {
                    paramConfig.pickupOnlyMode = orderingContext.selectedTable.pickupOnlyMode;
                }
                if (orderingContext.selectedTable.deliveryOnlyMode != undefined) {
                    paramConfig.deliveryOnlyMode = orderingContext.selectedTable.deliveryOnlyMode;
                }
            }
        } else if (orderingContext.selectedStore && orderingContext.selectedStore.tableNumber) {
            paramConfig.tableNumber = orderingContext.selectedStore.tableNumber;
        }
        this.paramConfigService.setConfig(paramConfig);
    }

    private async applyPreselectedTableNumberToSelectedStore(selectedStore: StoreData): Promise<StoreData> {
        if (this._preselectedTableNumber && selectedStore.orderAtTableStore && (this._preselectedOrderType === OrderType.ORDER_AT_TABLE || !(this._pickupOnlyMode && selectedStore.orderAheadStore))) {
            selectedStore = <StoreData>EntityData.create(StoreData).assign(selectedStore);
            this.setOrderType(OrderType.ORDER_AT_TABLE);
            await this.applyTableNumberToStore(this._preselectedTableNumber, selectedStore);
        } else if (this._preselectedOrderType && selectedStore.canOrder(this._preselectedOrderType)) {
            this.setOrderType(this._preselectedOrderType);
        }
        return selectedStore;
    }

    private updateCurrentStoreLocation(): void {
        this._currentStoreLocation = this._singleStoreLocation ? this._singleStoreLocation
            : this._currentStore ? new StoreLocation(this._currentStore.name, this._currentStore.locationName, this._stores.filter((store: StoreData) => store.locationName === this._currentStore.locationName))
                : undefined;
    }

    private async confirmOrderTypeChange(orderType: OrderType): Promise<boolean> {
        if (!orderType || this._orderType === orderType) {
            return true;
        }

        // Check for entitlement that is not available for this order type
        if (this._dealModel.currentEntitlementStatus === EntitlementStatus.APPLIED &&
            !DealEvents.currentEntitlement$.value.isAvailableForOrderType(orderType) &&
            !await this.notificationService.displayConfirmation("woa.heading.order.confirmRemoveUnavailableDeal", undefined,
                orderType === OrderType.ORDER_AHEAD ? "woa.message.order.dealRemoveUnavailableOrderAhead"
                    : orderType === OrderType.ORDER_DELIVERY ? "woa.message.order.dealRemoveUnavailableOrderDelivery"
                        : "woa.message.order.dealRemoveUnavailableOrderAtTable",
                "woa.button.common.cancel",
                "woa.button.common.proceed", true)) {
            return false;
        }

        return this._orderModel.removeUnavailableOrderItems({
            orderType: orderType,
            confirm: true
        });
    }

    public async confirmExpectedCompletionChange(previousExpectedCompletion: Date): Promise<void> {
        try {
            this.activeSchedulesSearchModel.filter["orderDate"] = this.expectedCompletionAsString;
            await this.activeSchedulesSearchModel.retrieve();
            if (!await this._orderModel.removeUnavailableOrderItems({
                schedules: true,
                confirm: true
            })) {
                this.expectedCompletion = previousExpectedCompletion;
                await this.refreshActiveSchedules();
            }
        } catch (error) {
            this.expectedCompletion = previousExpectedCompletion;
            await this.refreshActiveSchedules();
            throw error;
        } finally {

            // When changing the order required date/time, we need to reload the menu due to specials
            if (this.expectedCompletion !== previousExpectedCompletion) {
                // noinspection ES6MissingAwait
                this._menuModel.loadMenu("order required date/time changed");
            }
        }
    }

    public async refreshActiveSchedules(): Promise<any> {
        this.activeSchedulesSearchModel.filter["orderDate"] = this.expectedCompletionAsString;
        await this.activeSchedulesSearchModel.retrieve();
        return this._orderModel.removeUnavailableOrderItems({schedules: true});
    }

    /**
     * Returns the current store for an order type
     * @param orderType
     */
    public getCurrentStoreForOrderType(orderType: OrderType): StoreData {
        return this._currentStoreLocation ? this._currentStoreLocation.getStoreForOrderType(orderType) : this._currentStore;
    }

    public get isStoreOpenAtCompletionTime(): boolean {
        return this._currentStore && this.expectedCompletion ?
            this.expectedCompletion >= EntityData.parseLocalDateTime(this._currentStore.startLocalDateTime) && this.expectedCompletion < EntityData.parseLocalDateTime(this._currentStore.endLocalDateTime)
            : true;
    }

    public async promptForTip(): Promise<boolean> {
        if (this._tipSelected || !this._currentStore.allowTips || !this._currentStore.highlightTips) {
            return true;
        }
        return this.navigationService.createPage("woa-tip-selection-page");
    }

    public async promptForFirstName(transferTargetName: string): Promise<boolean> {
        if (this._firstName) {
            return true;
        }
        const name: string = await this.navigationService.createPage("woa-name-edit-page", {
            transferTargetName: transferTargetName
        });
        if (name) {
            this.firstName = name;
            this._firstNameTrusted = true;
            return true;
        }
        return false;
    }

    public async confirmPhoneNumberForSms(mobilePhone: string = this._mobilePhone, promptIfMissing: boolean = false): Promise<boolean> {
        if (mobilePhone) {
            mobilePhone = mobilePhone.replace(/\s/g, "");
        }

        // Do nothing for a missing phone number unless a prompt is required (typically only for integrated payments, where a phone number is expected to be returned)
        if (!mobilePhone && !promptIfMissing) {
            return true;
        }

        // No need to confirm phone number if we won't be using it for an order ready notification.
        // Note that order received notifications are no longer considered important enough to require prompting.
        if (!this.sessionModel.currentApp.sendOrderReadySms ||
            (this.orderType === OrderType.ORDER_AT_TABLE && !this._currentStore.sendTableOrderReady) ||
            this.sessionModel.isValidMobileNumberForSms(mobilePhone)) {
            if (mobilePhone && mobilePhone !== this._mobilePhone) {
                this._mobilePhone = mobilePhone;
                this._mobilePhoneTrusted = true;
            }
            return true;
        }

        this.loadingService.dismissSpinner();
        const result: {
            confirmed: boolean,
            mobilePhone?: string
        } = await this.navigationService.createPage("woa-phone-edit-page", {mobilePhone: mobilePhone});
        if (result && result.confirmed) {
            this._mobilePhone = result.mobilePhone;
            this._mobilePhoneTrusted = true;
            return true;
        }
        return false;
    }
}



// WEBPACK FOOTER //
// ./src/app/ordering-context/ordering-context.model.ts